/**
 * PetroNexus Analytics Client
 *
 * Queues events locally and flushes them in batches every 10 seconds.
 * Designed to be low-overhead and non-blocking — analytics should never
 * interrupt the main product experience.
 *
 * Usage:
 *   import { track } from '@/lib/analytics';
 *   track('fuel_transaction_created', { liters: 500, fuel_grade: 'D50' });
 */

const API_BASE = `${import.meta.env.VITE_API_URL ?? 'http://localhost:3000'}/api`;
const FLUSH_INTERVAL_MS = 10_000;
const MAX_QUEUE_SIZE = 100;

interface TrackEventPayload {
  event: string;
  properties?: Record<string, unknown>;
  sessionId?: string;
}

// In-memory queue
let queue: TrackEventPayload[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let sessionId: string | null = null;

const SESSION_ID_KEY = 'petronexus_sid';

/** Get or create a UUID v4 session ID for this browser session */
function getSessionId(): string {
  if (sessionId) return sessionId;

  // Try to restore from sessionStorage so the same session ID persists across
  // soft navigations within the same browser tab.
  const stored = sessionStorage.getItem(SESSION_ID_KEY);
  if (stored) {
    sessionId = stored;
    return sessionId;
  }

  // Generate a proper UUID v4 — crypto.randomUUID is available in all modern browsers.
  // Fallback keeps cryptographic randomness so analytics never relies on Math.random().
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    sessionId = crypto.randomUUID();
  } else if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0'));
    sessionId = [
      hex.slice(0, 4).join(''),
      hex.slice(4, 6).join(''),
      hex.slice(6, 8).join(''),
      hex.slice(8, 10).join(''),
      hex.slice(10, 16).join(''),
    ].join('-');
  } else {
    sessionId = `${Date.now().toString(16)}-analytics-session`;
  }

  sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  return sessionId;
}

/** Flush the queue to the API */
async function flush(): Promise<void> {
  if (queue.length === 0) return;

  const token = localStorage.getItem('token');
  if (!token) {
    // Not authenticated — discard queue silently
    queue = [];
    return;
  }

  const batch = [...queue];
  queue = [];

  try {
    await fetch(`${API_BASE}/analytics/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ events: batch }),
    });
  } catch {
    // Silently discard on failure — analytics must never break the app
  }
}

/** Schedule a flush if not already scheduled */
function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Track an analytics event.
 * Events are queued and sent in batches every 10 seconds.
 */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (queue.length >= MAX_QUEUE_SIZE) return; // Prevent unbounded growth

  queue.push({
    event,
    properties,
    sessionId: getSessionId(),
  });

  scheduleFlush();
}

/**
 * Track a session start. Call this once when the user authenticates.
 */
export function trackSessionStart(platform = 'web'): void {
  track('session_started', { platform });
}

/**
 * Track a session end. Call this on beforeunload.
 */
export function trackSessionEnd(durationMs: number): void {
  track('session_ended', { duration_ms: durationMs });
  // Flush immediately on session end using sendBeacon for reliability
  const token = localStorage.getItem('token');
  if (!token || queue.length === 0) return;

  const batch = [...queue];
  queue = [];

  const payload = JSON.stringify({ events: batch });

  // sendBeacon doesn't support custom headers (no Auth), so use fetch with keepalive
  void fetch(`${API_BASE}/analytics/events`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: payload,
    keepalive: true,
  }).catch(() => undefined);
}

/**
 * Force an immediate flush (e.g. on page unload or important action).
 */
export function flushNow(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  void flush();
}
